import React from 'react'
import { motion } from 'framer-motion'
import Seo from '../components/Seo'

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      content: [
        'Personal Information: Name, email address, phone number, and billing information when you create an account or make a purchase.',
        'Health & Fitness Data: Workout preferences, fitness goals, progress measurements, and health information you voluntarily provide.',
        'Usage Data: How you interact with our website, pages visited, time spent, and features used.',
        'Device Information: IP address, browser type, operating system, and device identifiers.',
        'Communication Data: Messages, feedback, and correspondence you send to us.'
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      content: [
        'Provide personalized fitness training and meal planning services.',
        'Process payments and manage your account and subscriptions.',
        'Communicate with you about services, appointments, and updates.',
        'Improve our services based on usage patterns and feedback.',
        'Send marketing communications (with your consent).',
        'Ensure platform security and prevent fraudulent activities.',
        'Comply with legal obligations and resolve disputes.'
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing',
      content: [
        'We do not sell, trade, or rent your personal information to third parties.',
        'Service Providers: We may share data with trusted third-party providers who assist in delivering our services (payment processors, email services, etc.).',
        'Legal Requirements: We may disclose information when required by law or to protect our rights and safety.',
        'Business Transfers: In the event of a merger or acquisition, your information may be transferred as part of the business assets.',
        'With Your Consent: We may share information for other purposes with your explicit consent.'
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      content: [
        'We implement industry-standard security measures to protect your personal information.',
        'All sensitive data is encrypted during transmission using SSL/TLS protocols.',
        'Access to personal information is restricted to authorized personnel only.',
        'Regular security audits and updates are performed to maintain data protection.',
        'We use secure payment processors that comply with PCI DSS standards.',
        'While we strive to protect your information, no method of transmission over the internet is 100% secure.'
      ]
    }
  ]

  const rights = [
    {
      title: 'Access & Correction',
      text: 'You have the right to access, update, or correct your personal information at any time through your account settings.'
    },
    {
      title: 'Data Portability',
      text: 'You can request a copy of your personal data in a structured, commonly used format.'
    },
    {
      title: 'Deletion',
      text: 'You may request deletion of your personal information, subject to certain legal and business requirements.'
    },
    {
      title: 'Opt-Out',
      text: 'You can opt out of marketing communications at any time by following the unsubscribe instructions.'
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Privacy Policy"
        description="How Fit by Cassie collects, uses, and protects your personal information."
      />

      {/* Header band */}
      <section className="bg-ink text-white">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="eyebrow-gold justify-center mb-4">Legal</span>
            <h1 className="font-display text-4xl md:text-5xl font-semibold mb-5">
              Privacy Policy
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Your privacy is important to us. This policy explains how we collect,
              use, and protect your personal information.
            </p>
            <div className="mt-6 text-sm text-gray-500">
              <p>Last updated: July 28, 2025</p>
              <p>Contact: Cassie Stutsman · hello@fitbycassie.com</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Document */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto px-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">Introduction</h2>
            <div className="divider-gold mb-6" />
            <p className="text-gray-700 leading-relaxed mb-4">
              At Fit by Cassie, we are committed to protecting your privacy and ensuring the security of your personal information.
              This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you visit our website
              or use our fitness training services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our services, you agree to the collection and use of information in accordance with this policy.
              If you do not agree with our policies and practices, please do not use our services.
            </p>
          </motion.div>

          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-14"
            >
              <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                {section.title}
              </h2>
              <div className="divider-gold mb-6" />
              <ul className="space-y-3">
                {section.content.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-brand rounded-full mt-2.5 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          {/* Your Rights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">Your Rights</h2>
            <div className="divider-gold mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {rights.map((right) => (
                <div key={right.title} className="bg-cream rounded-2xl p-6">
                  <h3 className="font-semibold text-ink mb-2">{right.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{right.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">Cookies and Tracking</h2>
            <div className="divider-gold mb-6" />
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website.
              Cookies help us remember your preferences, analyze site traffic, and provide personalized content.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You can control cookies through your browser settings. However, disabling cookies may affect
              the functionality of certain features on our website.
            </p>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-ink text-white rounded-3xl p-8 sm:p-10"
          >
            <h2 className="font-display text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-gray-400 leading-relaxed mb-5">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-1.5">
              <p className="font-medium">Cassie Stutsman</p>
              <p className="text-gray-400">Email: hello@fitbycassie.com</p>
              <p className="text-gray-400">Website: fitbycassie.com</p>
            </div>
          </motion.div>

          <p className="text-gray-500 text-sm text-center mt-12">
            This Privacy Policy is effective as of July 28, 2025, and will remain in effect except with respect to any changes in its provisions in the future,
            which will be in effect immediately after being posted on this page.
          </p>
        </div>
      </section>
    </div>
  )
}

export default PrivacyPolicy
